export type Hsv = { h: number; s: number; v: number };
export type ColourChoice = number | string | null;

export const BRAND_COLOURS = [
  { label: 'Ocean teal', hex: '#0f464f' },
  { label: 'Mint', hex: '#4efeb9' },
  { label: 'Jet black', hex: '#0f0c08' },
  { label: 'Sandy beige', hex: '#f9f7f1' },
  { label: 'Compass white', hex: '#ffffff' },
];

export const CHART_PALETTES = [
  {
    id: 'classic',
    label: 'Classic',
    colours: ['#0f464f', '#1192e8', '#b28600', '#6929c4', '#cc79a7', '#b0007a', '#0072b2', '#ee538b'],
  },
  {
    id: 'vibrant',
    label: 'Vibrant',
    colours: ['#0072b2', '#d55e00', '#009e73', '#cc79a7', '#e69f00', '#6d4eb3', '#008e9b', '#b33e5c'],
  },
  {
    id: 'muted',
    label: 'Muted',
    colours: ['#526f73', '#a9785c', '#728b65', '#8b789a', '#b18e4f', '#8a7471', '#698599', '#9a787f'],
  },
  {
    id: 'contrast',
    label: 'Deep contrast',
    colours: ['#005b7f', '#b84100', '#006e50', '#a82c75', '#805600', '#4d34a2', '#006879', '#a12735'],
  },
];

export const clampUnit = (value: number) => Math.min(1, Math.max(0, value));

export function chartPaletteColours(dark: boolean): Array<string> {
  if (dark) return ['#f6f4ee', '#78a9ff', '#f1c21b', '#da7e53', '#bd83b5', '#e5b8d5', '#39a695', '#6dd2fe'];
  return CHART_PALETTES[0].colours;
}

export function normaliseHex(value: string): string | undefined {
  const plain = value.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(plain))
    return `#${plain
      .split('')
      .map((part) => part + part)
      .join('')
      .toLowerCase()}`;
  return /^[0-9a-f]{6}$/i.test(plain) ? `#${plain.toLowerCase()}` : undefined;
}

export function hsvToHex({ h, s, v }: Hsv): string {
  const hue = (((h % 360) + 360) % 360) / 60;
  const chroma = clampUnit(v) * clampUnit(s);
  const x = chroma * (1 - Math.abs((hue % 2) - 1));
  const m = clampUnit(v) - chroma;
  const segments = [
    [chroma, x, 0],
    [x, chroma, 0],
    [0, chroma, x],
    [0, x, chroma],
    [x, 0, chroma],
    [chroma, 0, x],
  ];
  return `#${segments[Math.floor(hue)]
    .map((part) =>
      Math.round((part + m) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

export function hexToHsv(hex: string): Hsv {
  const plain = normaliseHex(hex)?.slice(1) ?? '0f464f';
  const [r, g, b] = [0, 2, 4].map((at) => parseInt(plain.slice(at, at + 2), 16) / 255);
  const high = Math.max(r, g, b);
  const low = Math.min(r, g, b);
  const delta = high - low;
  const h =
    delta === 0
      ? 0
      : high === r
        ? 60 * (((g - b) / delta) % 6)
        : high === g
          ? 60 * ((b - r) / delta + 2)
          : 60 * ((r - g) / delta + 4);
  return { h: (h + 360) % 360, s: high === 0 ? 0 : delta / high, v: high };
}

/** Match CSS conic-gradient: red starts at twelve o'clock, then clockwise. */
export function hueAtPoint(x: number, y: number): number {
  return ((Math.atan2(x, -y) * 180) / Math.PI + 360) % 360;
}

export function contrastRatio(first: string, second: string): number {
  const luminance = (hex: string) => {
    const plain = normaliseHex(hex)?.slice(1) ?? 'ffffff';
    const [r, g, b] = [0, 2, 4].map((at) => {
      const channel = parseInt(plain.slice(at, at + 2), 16) / 255;
      return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const a = luminance(first);
  const b = luminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}
